import { useEffect, useState } from 'react'
import {
  type Sample,
  TopBar,
  AppLayout,
  useGetSampleByIdQuery,
  useGetTaskByIdQuery,
  Mtds,
  isSampleEditable,
  isSamplePartiallyEditable,
  geometryToLongitudeLatitude,
  UserFeedback
} from 'prove-common'
import { useNavigate, useParams } from 'react-router'
import { SampleEditForm } from '../components/sample-edit-form/SampleEditForm'
import { SampleInfoCard } from '../components/sample-info-card/SampleInfoCard'
import { AppLogo } from '../components/app-logo/AppLogo'
import LocationFinderCard from '../components/location-finder-card/LocationFinderCard'

export const SampleEditScreen = () => {
  const navigate = useNavigate()
  const { sampleId } = useParams()
  const {
    data: sample,
    isPending: isPendingSample,
    isError: isErrorSample
  } = useGetSampleByIdQuery(sampleId)
  const { data: task } = useGetTaskByIdQuery(sample?.taskId)
  const isEditable = isSampleEditable(sample)
  const isPartiallyEditable = isSamplePartiallyEditable(sample)
  const canEdit = isEditable || isPartiallyEditable
  const breadcrumbItems = [
    {
      label: 'Prøver',
      to: '/samples'
    },
    {
      label: '#' + sampleId,
      to: `/samples/${sampleId}`
    },
    {
      label: 'Rediger'
    }
  ]

  const taskDescription = task?.description
  const matrix =
    task?.analysisType?.sampleType?.matrix ?? sample?.sampleType?.matrix

  const [selectedLonLat, setSelectedLonLat] = useState<
    [number, number] | undefined
  >(undefined)

  useEffect(() => {
    setSelectedLonLat(undefined)
  }, [sampleId])

  const initialLonLat = sample
    ? geometryToLongitudeLatitude(sample.geometry)
    : undefined

  const activeLonLat =
    selectedLonLat != null
      ? { longitude: selectedLonLat[0], latitude: selectedLonLat[1] }
      : initialLonLat

  const position = activeLonLat
    ? ({
        coords: {
          latitude: activeLonLat.latitude,
          longitude: activeLonLat.longitude
        }
      } as GeolocationPosition)
    : undefined

  return (
    <AppLayout
      isLoading={isPendingSample}
      isError={isErrorSample}
      topBar={<TopBar logo={<AppLogo />} breadcrumbItems={breadcrumbItems} />}
    >
      {sample != null && canEdit && (
        <>
          {task?.locationRequired && !isPartiallyEditable && (
            <LocationFinderCard
              locationRequired={true}
              position={position}
              sampleId={sampleId}
              isExistingSample={true}
              deferSave={true}
              onSelectedLonLatChanged={setSelectedLonLat}
            />
          )}

          <SampleInfoCard
            sampleId={sampleId}
            matrix={matrix}
            description={taskDescription}
          />

          <Mtds.Card as={Mtds.Grid} data-gap={5}>
            <Mtds.Heading as='h3'>Rediger skjema</Mtds.Heading>
            <SampleEditForm
              sample={sample}
              newTask={undefined}
              localLonLat={selectedLonLat}
              onSuccess={(oldSample: Sample) =>
                navigate(`/samples/${sampleId}/edit-completed`, {
                  state: { oldSample }
                })
              }
            />
          </Mtds.Card>
        </>
      )}
      <UserFeedback appName='proveta' />
    </AppLayout>
  )
}
